import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS } from '../../rendering/Renderer.js';
import { TurnsVortexTutorialState } from './TurnsTutorialAdapter.js';

type VState = TurnsVortexTutorialState;

// Gate pair B2 ↔ E3 (set by the adapter). Solution C0cD0cD2a|B2E3 — 3 turns, 17 cells,
// score 317, clearing 0 → 1 → 2 → 3. The gate is what carries the cart into row 3, where
// digit 2 sits: with the pair inactive the same turns clear only 0, 1 and 3.
const PARTIAL = 'C0cD0c';
const PARTIAL_GATED = `${PARTIAL}|B2E3`;
const NEAR = 'C0cD0cD2a';
const SOLVED = `${NEAR}|B2E3`;

// Activation scene: the main pair B2↔E3 sits on empty cells (activatable), while a second
// pair B5↔E5 is blocked by a turn on B5 (rendered amber, can't activate).
const ACTIVATE_BLOCKER = 'B5c';
const ACTIVATE_GATES: Array<[string, string]> = [['B5', 'E5']];

const scenes: TutorialScene[] = [
    // Scene 1: Toggle Gates — animate the gate placement flow
    {
        gameState: { turns: PARTIAL } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click Activate Vortex\nto place a gate pair', cell: 'B4', anchor: 'center' },
            { type: 'text-label', text: 'Pair must be linked\nin the Vortex Hub', cell: 'E5', anchor: 'center' },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { turns: PARTIAL },
                { turns: PARTIAL, gateMode: true },
                { turns: PARTIAL_GATED, gateMode: true },
                { turns: PARTIAL_GATED },
            ] as VState[],
        },
    },

    // Scene 2: Activation — toggle an unblocked pair active; a blocked pair stays amber
    {
        gameState: { turns: ACTIVATE_BLOCKER, gateMode: true, extraGates: ACTIVATE_GATES } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a pair\nto activate it', cell: 'D2', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Turns block\ngate activation', cell: 'B5', anchor: 'far-above', color: COLORS.amber },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { turns: ACTIVATE_BLOCKER, gateMode: true, extraGates: ACTIVATE_GATES },
                { turns: `${ACTIVATE_BLOCKER}|B2E3`, gateMode: true, extraGates: ACTIVATE_GATES },
            ] as VState[],
        },
    },

    // Scene 3: Cart teleportation. Animating the same turns with the pair off, then on,
    // shows what the gate actually buys: with it off the drive never reaches row 3, so
    // digit 2 goes uncleared; with it on the cart jumps and sweeps row 3.
    {
        gameState: { turns: SOLVED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Cart enters\na gate...', cell: 'B2', anchor: 'far-above' },
            { type: 'text-label', text: '...exits from\nthe other side', cell: 'E3', anchor: 'far-below' },
            { type: 'pulse', cell: 'B2', color: COLORS.purple },
            { type: 'pulse', cell: 'E3', color: COLORS.purple },
        ],
        animate: {
            interval: 1800,
            loop: true,
            frames: [
                { turns: NEAR },
                { turns: SOLVED },
            ] as VState[],
        },
    },

    // Scene 4: Solved — the heading survives the jump, and the pair is driven both ways
    {
        gameState: { turns: SOLVED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'The cart keeps its\nheading through a gate', cell: 'B1', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Gates can be used\nmore than once', cell: 'E4', anchor: 'center' },
            { type: 'pulse', cell: 'B2', color: COLORS.purple },
            { type: 'pulse', cell: 'E3', color: COLORS.purple },
        ],
    },
];

/**
 * Vortex Turns tutorial: 4 scenes on a 6×6 vortex grid with digits 0-3.
 * Teaches gate creation, activation, and cart teleportation through Vortex gates.
 * See docs/tutorials/turns.md for detailed scene descriptions.
 */
export const TURNS_VORTEX_TUTORIAL: TutorialDefinition = {
    gameType: 'turns-vortex',
    gridCode: '6/0-3:0C4,1F2,2A3,3D1',
    scenes,
    previewScene: 3,
};
